import React, { forwardRef, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Loader from '../../general/Loader';
import Icon from '../../general/Icon';
import Text from '../../typography/Text';

import { createUseStyles, useTheme } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Button');

const BUTTON_TYPES = {
    primary: 'primary',
    primarySoft: 'primary-soft',
    primaryError: 'primary-error',
    secondary: 'secondary',
    secondaryError: 'secondary-error',
    terciary: 'terciary',
    dashed: 'dashed',
    promotion: 'promotion',
    text: 'text',
};

const LOADER_SIZES = {
    big: 'tiny',
    medium: 'tiny',
    small: 'mini',
};

const BUTTON_LOADER_COLORS = {
    [BUTTON_TYPES.primary]: 'white',
    [BUTTON_TYPES.primaryError]: 'white',
    [BUTTON_TYPES.secondary]: 'primary',
    [BUTTON_TYPES.primarySoft]: 'primary',
    [BUTTON_TYPES.text]: 'primary',
    [BUTTON_TYPES.secondaryError]: 'danger',
    [BUTTON_TYPES.terciary]: 'actionMinor',
    [BUTTON_TYPES.dashed]: 'actionMinor',
    [BUTTON_TYPES.promotion]: 'primary',
};

const Button = forwardRef(
    (
        {
            children,
            classes: classesProp,
            overrides: overridesProp,
            className: classNameProp,
            onClick,
            isDisabled,
            isFullWidth,
            isLoading,
            customIcon,
            href,
            preserveCustomIconColors,
            icon,
            iconPosition = 'left',
            size = 'medium',
            target,
            type = 'primary',
            ...props
        },
        ref,
    ) => {
        const theme = useTheme();
        const classes = useClasses(useStyles, classesProp);
        // Overrides
        const override = getOverrides(overridesProp, Button.overrides);

        // Classes
        const rootClassName = classnames(
            classes.root,
            {
                [classes.primary]: type === 'primary',
                [classes.primaryError]: type === 'primary-error',
                [classes.secondary]: type === 'secondary',
                [classes.secondaryError]: type === 'secondary-error',
                [classes.terciary]: type === 'terciary',
                [classes.dashed]: type === 'dashed',
                [classes.promotion]: type === 'promotion',
                [classes.primarySoft]: type === 'primary-soft',
                [classes.text]: type === 'text',
                [classes[size]]: size,
                [classes.disabled]: isDisabled,
                [classes.loading]: isLoading,
                [classes.fullWidth]: isFullWidth,
                [classes.hasCustomIcon]: !!customIcon,
                [classes.preserveIconColors]: customIcon && preserveCustomIconColors,
                [classes.iconLeft]: (icon || customIcon) && iconPosition === 'left',
                [classes.iconRight]: (icon || customIcon) && iconPosition === 'right',
                [classes.iconOnly]: (icon || customIcon) && !children,
                [classes.href]: href,
            },
            classNameProp,
        );

        const rootProps = useMemo(
            () => ({
                ref,
                ...props,
                className: rootClassName,
                onClick: isDisabled || isLoading ? null : onClick,
            }),
            [props, ref, rootClassName, isDisabled, isLoading, onClick],
        );

        const iconProps = useMemo(() => {
            const properties = { size: 'medium', name: icon };
            switch (type) {
                case BUTTON_TYPES.text:
                case BUTTON_TYPES.secondary:
                    return {
                        ...properties,
                        color: theme.colors.actionMajor[500],
                    };
                case BUTTON_TYPES.secondaryError:
                    return {
                        ...properties,
                        color: theme.colors.semantic.negative500,
                    };
                case BUTTON_TYPES.dashed:
                case BUTTON_TYPES.terciary:
                    return {
                        ...properties,
                        color: theme.colors.textLight.secondary,
                    };
                case BUTTON_TYPES.promotion:
                    return {
                        ...properties,
                        color: theme.colors.textLight.primary,
                    };
                case BUTTON_TYPES.primary:
                case BUTTON_TYPES.primaryError:
                default:
                    return {
                        ...properties,
                        color: theme.colors.primary.white,
                    };
            }
        }, [icon, theme, type]);

        const content = (
            <div className={classes.content}>
                {isLoading && (
                    <div className={classes.loaderContainer}>
                        <Loader
                            size={LOADER_SIZES[size]}
                            color={BUTTON_LOADER_COLORS[type]}
                            {...override.Loader}
                        />
                    </div>
                )}
                {(icon || customIcon) && iconPosition === 'left' && (
                    <div className={classes.icon} {...override.icon}>
                        {customIcon || <Icon {...iconProps} />}
                    </div>
                )}
                {children && (
                    <Text className={classes.Text} {...override.Text}>
                        {children}
                    </Text>
                )}
                {(icon || customIcon) && iconPosition === 'right' && (
                    <div className={classes.icon} {...override.icon}>
                        {customIcon || <Icon {...iconProps} />}
                    </div>
                )}
            </div>
        );

        if (href) {
            return (
                <a href={href} target={target} {...rootProps} {...override.root}>
                    {content}
                </a>
            );
        } else {
            return (
                <button {...rootProps} type="button" {...override.root}>
                    {content}
                </button>
            );
        }
    },
);

Button.overrides = ['root', 'Text', 'Loader'];

Button.propTypes = {
    /** Content to be displayed inside the button */
    children: PropTypes.node,
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** Custom icon component (React node) to display instead of the icon library icon */
    customIcon: PropTypes.node,
    /** When true, preserves the custom icon's original colors instead of applying button colors */
    preserveCustomIconColors: PropTypes.bool,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Function called when the button is clicked */
    onClick: PropTypes.func,
    /** Determines the button size - affects padding, font size, and overall dimensions */
    size: PropTypes.oneOf(['big', 'medium', 'small']),
    /** Visual style variant of the button */
    type: PropTypes.oneOf([
        BUTTON_TYPES.primary, // Default, emphasized action
        BUTTON_TYPES.primarySoft, // Softer version of primary
        BUTTON_TYPES.primaryError, // For destructive primary actions
        BUTTON_TYPES.secondary, // Less emphasized action
        BUTTON_TYPES.secondaryError, // For destructive secondary actions
        BUTTON_TYPES.terciary, // Minimal emphasis
        BUTTON_TYPES.dashed, // With dashed border
        BUTTON_TYPES.promotion, // For promotional/special actions
        BUTTON_TYPES.text, // Text-only appearance
    ]),
    /** When true, the button cannot be clicked and appears grayed out */
    isDisabled: PropTypes.bool,
    /** When true, button will take up 100% of the available width */
    isFullWidth: PropTypes.bool,
    /** When true, displays a loading spinner and disables clicks */
    isLoading: PropTypes.bool,
    /** Renders the button as an anchor tag with this URL */
    href: PropTypes.string,
    /** Specifies where to open the linked document (for href buttons) */
    target: PropTypes.string,
    /** Icon name to display from the icon library */
    icon: PropTypes.string,
    /** Controls the position of the icon relative to the button text */
    iconPosition: PropTypes.oneOf(['left', 'right']),
};

export default memo(Button);
